import React from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Customer, PersonCustomer, CompanyCustomer } from '../../services/clientsService';
import { ClientTypeBadge } from './ClientTypeBadge';
import { ClientStatusBadge } from './ClientStatusBadge';
import { formatCuit, formatPhone } from '../../utils/format';
import {
    User,
    Building2,
    Mail,
    Phone,
    MapPin,
    FileText,
    UserPlus,
    RefreshCw,
} from 'lucide-react';

interface Step1ClientSelectionProps {
    selectedClient: Customer | null;
    onSelectClient?: () => void;
}

const InfoRow: React.FC<{ icon: React.ReactNode; label: string; value: string | null | undefined }> = ({ icon, label, value }) => (
    <div className="flex items-start gap-3">
        <div className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-muted flex items-center justify-center">
            {icon}
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-xs text-muted-foreground font-medium mb-0.5">{label}</p>
            <p className="text-sm text-foreground font-medium truncate">{value || '—'}</p>
        </div>
    </div>
);

const Step1ClientSelection: React.FC<Step1ClientSelectionProps> = ({
    selectedClient,
    onSelectClient
}) => {
    const renderPersonClient = (client: PersonCustomer) => {
        return (
            <div className="space-y-5">
                {/* Avatar + Name Header */}
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="text-xl font-bold text-foreground truncate">
                                {client.firstName} {client.lastName}
                            </h3>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <ClientStatusBadge isActive={client.isActive} />
                            <ClientTypeBadge type="PERSON" />
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-border" />

                {/* Info Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoRow
                        icon={<FileText className="w-4 h-4 text-muted-foreground" />}
                        label="CUIT / CUIL"
                        value={formatCuit(client.cuit)}
                    />
                    <InfoRow
                        icon={<Mail className="w-4 h-4 text-muted-foreground" />}
                        label="Email"
                        value={client.email}
                    />
                    <InfoRow
                        icon={<Phone className="w-4 h-4 text-muted-foreground" />}
                        label="Teléfono"
                        value={formatPhone(client.phone || '')}
                    />
                    <InfoRow
                        icon={<MapPin className="w-4 h-4 text-muted-foreground" />}
                        label="Dirección"
                        value={client.address}
                    />
                </div>
            </div>
        );
    };

    const renderCompanyClient = (client: CompanyCustomer) => {
        return (
            <div className="space-y-5">
                {/* Avatar + Name Header */}
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-bold text-foreground truncate mb-1">
                            {client.businessName}
                        </h3>
                        <div className="flex items-center gap-2 flex-wrap">
                            <ClientStatusBadge isActive={client.isActive} />
                            <ClientTypeBadge type="COMPANY" />
                            {client.companyType && (
                                <Badge variant="outline" className="text-xs font-normal">
                                    {client.companyType}
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <div className="border-t border-border" />

                {/* Company Info */}
                <div>
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                        Información Empresarial
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <InfoRow
                            icon={<FileText className="w-4 h-4 text-muted-foreground" />}
                            label="CUIT"
                            value={formatCuit(client.cuit)}
                        />
                        <InfoRow
                            icon={<MapPin className="w-4 h-4 text-muted-foreground" />}
                            label="Dirección Legal"
                            value={client.legalAddress || client.address}
                        />
                        {client.email && (
                            <InfoRow
                                icon={<Mail className="w-4 h-4 text-muted-foreground" />}
                                label="Email"
                                value={client.email}
                            />
                        )}
                        {client.phone && (
                            <InfoRow
                                icon={<Phone className="w-4 h-4 text-muted-foreground" />}
                                label="Teléfono"
                                value={client.phone}
                            />
                        )}
                    </div>
                </div>

                {/* Contact Person */}
                {(client.contactName || client.contactEmail || client.contactPhone) && (
                    <div className="pt-1">
                        <div className="border-t border-border mb-4" />
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                            Persona de Contacto
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {client.contactName && (
                                <InfoRow
                                    icon={<User className="w-4 h-4 text-muted-foreground" />}
                                    label="Nombre"
                                    value={client.contactName}
                                />
                            )}
                            {client.contactEmail && (
                                <InfoRow
                                    icon={<Mail className="w-4 h-4 text-muted-foreground" />}
                                    label="Email"
                                    value={client.contactEmail}
                                />
                            )}
                            {client.contactPhone && (
                                <InfoRow
                                    icon={<Phone className="w-4 h-4 text-muted-foreground" />}
                                    label="Teléfono"
                                    value={client.contactPhone}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="p-6 lg:p-8">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">Información del Cliente</h2>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            {selectedClient ? 'Cliente seleccionado para esta venta' : 'Seleccione el cliente para continuar'}
                        </p>
                    </div>
                    {selectedClient && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={onSelectClient}
                            className="gap-2 shrink-0"
                        >
                            <RefreshCw className="w-3.5 h-3.5" />
                            <span className="hidden sm:inline">Cambiar</span>
                        </Button>
                    )}
                </div>

                {selectedClient ? (
                    <div>
                        {selectedClient.type === 'PERSON'
                            ? renderPersonClient(selectedClient as PersonCustomer)
                            : renderCompanyClient(selectedClient as CompanyCustomer)
                        }
                    </div>
                ) : (
                    /* Empty state */
                    <div className="flex flex-col items-center justify-center py-16 text-center">
                        <div className="mb-6 w-20 h-20 rounded-2xl bg-muted flex items-center justify-center">
                            <User className="w-10 h-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-semibold text-foreground mb-1">
                            Ningún cliente seleccionado
                        </h3>
                        <p className="text-sm text-muted-foreground mb-6 max-w-sm">
                            Para continuar con la venta, seleccione un cliente de la lista.
                        </p>
                        <Button onClick={onSelectClient} className="gap-2">
                            <UserPlus className="w-4 h-4" />
                            Seleccionar Cliente
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Step1ClientSelection;
